// utils/deepLinks.js
//
// One parser for VibeLink app links, shared by App.js (Universal Links +
// vibelink:// from outside the app) and in-app link taps (chat messages and
// link previews). iOS never hands a links.try-vibelink.com URL back to the app
// that opened it with Linking.openURL — it opens Safari — so in-app taps try
// tryOpenInApp() first. Pure module (no react-native import) so it has node
// tests.

// Public share URL for a club (Universal Link; the QR, Share Link and the
// club profile Share chip all use this one form).
export const clubShareLink = (clubId) => `https://links.try-vibelink.com/c/${clubId}`;

// Public share URL for an event (rich-preview page; Universal Link opens the app).
export const eventShareLink = (eventId) => `https://links.try-vibelink.com/e/${eventId}`;

// Universal Links: links.try-vibelink.com/e/<eventId> (or /c/<clubId>) is
// intercepted by iOS and handed straight here instead of loading the web
// page, once the apple-app-site-association file (hosted there) and this
// app's Associated Domains entitlement agree (see VibeLink.entitlements).
// vibelink://e/<id> (or c/<id>) is the other legitimate form, and the
// primary handoff path from links/app.py's own landing pages — iOS does not
// re-trigger a Universal Link from a page served by that same domain, so the
// "Open in VibeLink" button there uses the custom scheme instead.
//
// Anchored on scheme+host rather than matching "/e/<id>" anywhere in the
// string: an unanchored match would also fire for an unrelated URL that
// merely contains that substring (e.g. forwarded through a third-party
// redirector's query param). Not a security hole either way — the captured
// id only ever reaches a Firestore doc() reference, and [^/?#]+ structurally
// forbids path traversal — but there's no reason to be sloppy about it.
export function parseDeepLink(url) {
  if (!url) return null;

  const eventMatch = url.match(
    /^(?:vibelink:\/\/|https:\/\/links\.try-vibelink\.com\/)e\/([^/?#]+)/
  );
  if (eventMatch) {
    return { name: "EventDetails", params: { eventId: eventMatch[1] } };
  }

  const clubMatch = url.match(
    /^(?:vibelink:\/\/|https:\/\/links\.try-vibelink\.com\/)c\/([^/?#]+)/
  );
  if (clubMatch) {
    return { name: "ClubDetails", params: { clubId: clubMatch[1] } };
  }

  const manageClubMatch = url.match(
    /^(?:vibelink:\/\/|https:\/\/links\.try-vibelink\.com\/)m\/([^/?#]+)/
  );
  if (manageClubMatch) {
    return { name: "ManageClub", params: { clubId: manageClubMatch[1] } };
  }

  // No match (App.js already logs every incoming link in dev).
  return null;
}

// App.js registers its handleUniversalLink here once at module load, so
// in-app taps navigate (or queue during a cold start) exactly like an
// external link does. Kept as a registration instead of importing App.js
// (that would be a cycle).
let inAppLinkHandler = null;

export function setInAppLinkHandler(handler) {
  inAppLinkHandler = typeof handler === "function" ? handler : null;
}

// True when `url` is a VibeLink route and was handed to the in-app handler;
// false means the caller should open it externally as before.
export function tryOpenInApp(url) {
  if (typeof url !== "string" || !inAppLinkHandler) return false;
  const trimmed = url.trim();
  if (!parseDeepLink(trimmed)) return false;
  inAppLinkHandler(trimmed);
  return true;
}
